import { useEffect } from 'react';
import Cookies from 'js-cookie';
import type { PlaygroundPayload } from '@linkie/shared';
import { usePlaygroundStore, usePlaygroundDispatch } from '../store/playground.store';
import { PlaygroundActionType } from '../types/playground-action.type';
import { useDefaultPageQuery, usePageQuery, usePageUsernamesQuery } from '../hooks/usePlaygroundQueries.hook';
import { isPublicPageRoute } from '@/core/utils/is-public-page-route';

const PlaygroundDataLoader = () => {
  const dispatch = usePlaygroundDispatch();
  const defaultPageQuery = useDefaultPageQuery();
  const pageUsernamesQuery = usePageUsernamesQuery();
  const getPageQuery = usePageQuery();

  useEffect(() => {
    const defaultPage = defaultPageQuery?.data?.data;
    if (!defaultPage) return;

    // The default page must never land on a public page. The query is disabled
    // on these routes, but React Query still hands back whatever this browser
    // cached while editing — dispatching it would paint the editor's page under
    // the visited username until the lookup overwrites it (or instead of it,
    // when this effect happens to run last).
    if (isPublicPageRoute()) return;

    const { payload, create_new_page } = usePlaygroundStore.getState().state;

    if (create_new_page || payload._id === defaultPage._id) return;

    dispatch({
      type: PlaygroundActionType.SET_INIT_PAYLOAD,
      payload: defaultPage as PlaygroundPayload,
    });

    Cookies.set('pageId', defaultPage._id || '');
  }, [defaultPageQuery.data, dispatch]);

  useEffect(() => {
    if (!pageUsernamesQuery?.data) return;

    dispatch({
      type: PlaygroundActionType.GET_LINKIE_USERNAMES,
      payload: pageUsernamesQuery.data.data,
    });
  }, [pageUsernamesQuery.data, dispatch]);

  useEffect(() => {
    if (!getPageQuery?.data) return;

    const page = getPageQuery.data.data;

    // A lookup that found nothing must wipe the store. `SET_INIT_PAYLOAD`
    // spreads its payload, so dispatching a null page is a no-op that leaves the
    // previously loaded page in place — which then renders (and gets a visit
    // recorded against it) under a username that does not exist.
    if (!page) {
      dispatch({ type: PlaygroundActionType.CLEAR_PAYLOAD });
      return;
    }

    dispatch({
      type: PlaygroundActionType.SET_INIT_PAYLOAD,
      payload: page as PlaygroundPayload,
    });
  }, [getPageQuery.data, dispatch]);

  return null;
};

export default PlaygroundDataLoader;
